import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Component } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ENTITLEMENTS } from './application-core/constants';
import ForgottenPassword from './core/access/ForgottenPassword';
import ForgottenUsername from './core/access/ForgottenUsername';
import Login from './core/access/Login';
import ResetPassword from './core/access/ResetPassword';
import Signup from './core/access/Signup';
import TooLateToReset from './core/access/TooLateToReset';
import AccountApplication from './core/account/AccountApplication';
import AdministrationApplication from './core/administration/AdministrationApplication';
import ApplicationsManager from './core/applications/ApplicationsManager';
import BasicBackground from './core/BasicBackground';
import BasicLayout from './core/BasicLayout';
import DashboardApplication from './core/dashboard/DashboardApplication';
import Dpa from './core/Dpa';
import Errored from './core/Errored';
import FirstVisit from './core/firstvisit/FirstVisit';
import GetStarted from './core/getstarted/GetStarted';
import RegistrationError from './core/getstarted/RegistrationError';
import TooLate from './core/getstarted/TooLate';
import Idle from './core/Idle';
import Loading from './core/Loading';
import Maintenance from './core/Maintenance';
import NotFound from './core/NotFound';
import PrivateRoute from './core/PrivateRoute';
import ImportWarningDialog from './core/project/ImportWarningDialog';
import UserProvider from './core/providers/UserProvider';
import { authenticationService } from './core/services/security/authenticationService';
import { gstheme } from './core/style/gstheme';
import SubscriptionSelectorDialog from './core/subscription/SubscriptionSelectorDialog';
import SubscriptionSuccess from './core/subscription/SubscriptionSuccess';
import Unsaved from './core/Unsaved';
import { ENTITLEMENTS as APPENTITLEMENTS, MANAGED_MODE, MODULES, STATUS_ACCEPTED, STATUS_PENDING, STATUS_REJECTED } from './core/utils/constants';
import GS from './grafsync/grafsync';
import OntologyApplication from './ontology/OntologyApplication';
import VocabularyApplication from './vocabulary/VocabularyApplication';
import DataApplication from './data/DataApplication';
import { Typography } from '@material-ui/core';
import { stubFalse } from 'lodash';

console.log("App loading now...");// eslint-disable-line

const theme = createTheme(gstheme);
const DEFAULT_TIME_TO_LOGOUT = 900000;

class App extends Component {

  constructor(props) {
    super(props);
    const success = window.location.pathname.substring(4).includes('success');
    let newSubscription;
    if (success) {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionID = urlParams.get('id');
      newSubscription = "https://graphifi.com/data/subscription/" + subscriptionID;
    }
    let initialURL = "";
    const pn = window.location.pathname;
    if (pn.startsWith("/gs") && !pn.startsWith("/gs/login")) {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionParam = urlParams.get('subscription');
      if (subscriptionParam) {
        initialURL = window.location.pathname + "?subscription=" + subscriptionParam + window.location.hash;
      }
      else {
        initialURL = window.location.pathname + window.location.hash;
      }
    }
    // We set isLoading to true as we try to get user authentication from refresh token
    this.state = {
      loggingOut: false,
      isLoading: true,
      initialURL,
      logOut: this.logOut,
      logOutDelay: this.logOutDelay,
      hideMaintenance: false,
      hideRelease: false,
      acknowledgeMaintenance: this.acknowledgeMaintenance,
      acknowledgeRelease: this.acknowledgeRelease,
      setUser: this.setUser,
      updateUser: this.updateUser,
      cancelSubscription: this.cancelSubscription,
      clearSubscription: this.clearSubscription,
      rejectInvitation: this.rejectInvitation,
      setSubscription: this.onSelectSubscription,
      subscriptionChosen: undefined,
      activeSubscription: undefined,
      cancelledSubscriptions: [],
      rejectedInvitations: [],
      activeSubscriptionName: "No subscription selected",
      entitlements: [],
      groups: [],
      subscriptions: [],
      lockedGraphs: [],
      invitations: [],
      modules: 0,
      expirationDate: undefined,
      isErrored: undefined,
      maintenance: false,
      sessionExpired: false,
      success,
      newSubscription,
      fatal: false,
      idle: false,
      idleTime: DEFAULT_TIME_TO_LOGOUT,
      firstVisit: false,
      importComplete: undefined,
      ignore: false,
      loadKey: "StartKey"
    };
  }

  // Catch outstanding edits still happening before window closes
  setupBeforeUnloadListener = () => {
    window.addEventListener("beforeunload", (ev) => {
      console.log("Unloading GrafSync ...");// eslint-disable-line
      if (GS.outstandingEdits() > 0) {
        console.log("Outstanding edits exist.");// eslint-disable-line
        ev.preventDefault();
        ev.returnValue = 'Outstanding edits: ' + GS.outstandingEdits();
      }
    });
  };

  acknowledgeMaintenance = () => this.setState({ hideMaintenance: true });

  acknowledgeRelease = () => {
    GS.updateUser(this.state.user.id, { acknowledgeRelease: true });
    this.setState({ hideRelease: true });
  };

  keep = () => {
    this.setState({ idle: false });
    this.idleStartTimeout = setTimeout(this.setupTimers, 60000);
  };

  idleWarning = () => {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    // If we're in error state at idle time just log out
    if (this.state.isErrored) {
      this.logOut();
    }
    else {
      this.setState({ idle: true });
    }
  };

  setupTimers = () => {
    if (this.idleStartTimeout) clearTimeout(this.idleStartTimeout);
    // Any click will reset the timings
    document.addEventListener("click", this.resetIdle);
    this.idleTimeout = setTimeout(this.idleWarning, this.state.idleTime - 60000);
  };

  logOutDelay = (delay) => {
    this.setState({ idleTime: delay * 60000 }, this.resetIdle);
  };

  resetIdle = () => {
    if (this.idleStartTimeout) clearTimeout(this.idleStartTimeout);
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    document.removeEventListener("click", this.resetIdle);
    // After a reset we spend 60s before setting up timer to avoid having to respond to click event all the time
    this.idleStartTimeout = setTimeout(this.setupTimers, 60000);
  };

  updateUser = (user) => {
    const newUser = Object.assign({}, this.state.user, user);
    this.setState({ user: newUser });
  };

  maintenanceBlock = () => {
    this.setState({ maintenance: true });
    this.logOut();
  };

  setUser = async (user, firstVisit, login = false) => {
    if (login && this.state.initialURL && this.state.initialURL !== "/gs/" && !this.state.initialURL.includes("/gs/getstarted")) {
      console.log("Going to redirect to " + this.state.initialURL)// eslint-disable-line
      window.location.href = this.state.initialURL;
      return;
    }
    const newUser = { ...user };
    if (!newUser.systemAdmin && newUser.adminWindow && newUser.adminWindow.startTime) {
      setTimeout(this.maintenanceBlock, newUser.adminWindow.startTime - Date.now() - 10000);
    }
    if (GS.isInitialised()) {
      // Make sure cleared out from any previous user
      await GS.exit();
    }
    this.setState({
      user: newUser,
      subscriptionChosen: this.state.ignore ? false : undefined,
      isLoading: true,
      ignore: false,
      idle: false,
      isErrored: undefined,
      hideMaintenance: false,
      idleTime: DEFAULT_TIME_TO_LOGOUT,
      hideRelease: user.acknowledgeRelease === process.env.REACT_APP_VERSION,
      subscriptions: [],
      rejectedInvitations: [],
      invitations: [],
      modules: 0,
      activeSubscription: undefined,
      activeSubscriptionName: "No subscription selected",
      entitlements: [],
      groups: [],
      fatal: false
    }, async () => {
      try {
        const endpoint = process.env.REACT_APP_API_URL || "";
        const apiRateLimit = window.Graphologi.apiRateLimit ? Number(window.Graphologi.apiRateLimit) : null;
        const didInitialise = await GS.initialise(user, endpoint, this.errorCallback, this.importCallback, this.lockCallback, apiRateLimit);
        // If errorCallback has been used we will be in an error state - this will be uncontrolled
        if (this.state.isErrored) {
          this.setState({
            fatal: true,
            isLoading: false,
            actionable: true
          });
          return;
        }
        // If didInitialise is false this implies that a controlled fail happened
        if (!didInitialise.hasOwnProperty("status") || didInitialise.status === false) {
          this.logOut();
          return;
        }
        if (GS.isInitialised()) {
          // We really only want to do this for test purposes
          if (process.env.NODE_ENV === 'development') {
            window.GS = GS;
          }
          this.idleStartTimeout = setTimeout(this.setupTimers, 60000);
          const subscriptions = await GS.subscriptions();
          const invitations = await GS.invitations();
          const acceptedInvitations = invitations.filter(invitation => invitation.status[0] === STATUS_ACCEPTED);
          const pendingInvitations = invitations.filter(invitation => invitation.status[0] === STATUS_PENDING);
          const onlyOneSubscription = pendingInvitations.length === 0
            && (subscriptions.length + acceptedInvitations.length) === 1
            && (subscriptions.length === 0 || (subscriptions.length === 1 && subscriptions[0].approved));
          const overrideFirstVisit = (subscriptions && subscriptions.length > 0)
            || (acceptedInvitations && acceptedInvitations.length > 0)
            || user.givenName || user.familyName
            || window.Graphologi.mode === MANAGED_MODE
            ? false : firstVisit;
          this.setState({
            subscriptions,
            invitations,
            firstVisit: overrideFirstVisit
          }, () => {
            if (onlyOneSubscription) {
              const id = subscriptions.length > 0 ? subscriptions[0].id : invitations[0].subscription;
              this.onSelectSubscription(id)
                .then(() => {
                  this.setState({ isLoading: false });
                });
            }
            else {
              const urlParams = new URLSearchParams(window.location.search);
              const subscriptionParam = urlParams.get('subscription');
              const isValidParam = subscriptions.some(s => s.id === subscriptionParam) || invitations.some(i => i.subscription === subscriptionParam);
              if (isValidParam && subscriptionParam) {
                this.onSelectSubscription(subscriptionParam)
                  .then(() => {
                    this.setState({ isLoading: false });
                  });
              }
              else {
                this.setState({ isLoading: false });
              }
            }
          });
        }
        else {
          this.setState({
            isErrored: {
              message: "Cannot initialise Graphologi system."
            },
            fatal: true,
            isLoading: false,
            actionable: true
          });
        }
      }
      catch (e) {
        console.log("GS initialisation issue: ", e);// eslint-disable-line
        this.logOut();
      }
    });
  };

  onNewSubscription = (subscriptionID) => {
    const subscription = "https://graphifi.com/data/subscription/" + subscriptionID;
    this.setState({ success: true, newSubscription: subscription, firstVisit: false, isLoading: false });
  };

  async getUserData() {
    return authenticationService.doRefresh();
  }

  componentDidMount() {
    this.setupBeforeUnloadListener();
    // Retrieve user details if user is already known from refresh token
    this.getUserData()
      .then(userData => {
        if (!userData) {
          this.setState({ isLoading: false });
          return;
        }
        else {
          // If trying to access URLs that should not be used then redirect to home page
          // We wouldn't expect admins to come in on anything but home page so if they get redirected so be it!
          const locationPart = this.state.initialURL.substring(4);
          const segment = locationPart.indexOf("/") > 0 ? locationPart.substring(0, locationPart.indexOf("/")) : locationPart;
          if (segment !== "" && !['dashboard', 'vocabularymanager', 'ontologymanager', 'datamanager', 'account', 'success'].includes(segment)) {
            window.location.href = "/";
          }
          else {
            this.setUser(userData);
          }
        }
      });
  }

  logOut = (ignoreOutstandingEdits = false) => {
    if (this.state.sessionExpired) {
      GS.exit().then(() => {
        window.location.href = "/";
      });
      return;
    }
    if (this.state.activeSubscription && !ignoreOutstandingEdits && GS.outstandingEdits() > 0) {
      this.setState({ processingEdits: true, continueOnSave: true });
      return;
    }
    if (this.idleStartTimeout) clearTimeout(this.idleStartTimeout);
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    const user = this.state.user;
    // May already be logged out so check if we still have a user
    clearTimeout(this.loggingOutTimeout);
    this.setState({ loggingOut: true }, async () => {
      if (user) await authenticationService.logout(user.ssoUser === true);
      // Make sure we reset everything
      this.setState(() => ({
        user: undefined,
        idle: false,
        ignore: false,
        isErrored: undefined,
        isLoading: false,
        hideMaintenance: false,
        subscriptionChosen: undefined,
        subscriptions: [],
        tripleCount: 0,
        idleTime: 900000,
        maxTriplesAllowed: 0,
        expirationDate: undefined,
        rejectedInvitations: [],
        invitations: [],
        activeSubscription: undefined,
        initialURL: "",
        activeSubscriptionName: "No subscription selected",
        entitlements: [],
        groups: [],
        modules: 0,
        fatal: false,
        firstVisit: stubFalse
      }));
      this.loggingOutTimeout = setTimeout(async () => {
        this.setState({ loggingOut: false });
      }, 2000);
    });
  };

  errorCallback = (message, fatal = false, actionable, sessionExpired) => {
    // We don't want errors clearing errors
    this.setState({
      isErrored: {
        message
      },
      sessionExpired,
      fatal,
      actionable
    });
  };

  watchUpload = () => {
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    this.hcTimeout = setTimeout(async () => {
      try {
        let status = await GS.uploadStatus(this.state.importId);
        // Keep looping until ready
        if (status.transfer === undefined) {
          this.watchUpload();
        }
        else {
          GS.unload();
          this.setState({ importComplete: true });
        }
      }
      catch (e) {
        this.setState({
          isErrored: true,
          errorMessage: e.message
        });
      }
    }, 10000);
  };

  resetImport = () => {
    this.setState({
      importId: undefined,
      importComplete: undefined
    });
  };

  importCallback = (id) => {
    // We don't want errors clearing errors
    this.setState({
      importId: id,
      importComplete: false
    });
    this.watchUpload();
  };

  lockCallback = (lockedGraphs, loadKey) => {
    const dedupedLockedGraphs = [...new Set(lockedGraphs)];
    this.setState({
      lockedGraphs: dedupedLockedGraphs,
      ...(loadKey) && { loadKey }
    });
  };

  clearError = () => {
    this.setState({
      isErrored: undefined,
      fatal: false,
      actionable: true
    });
  };

  ignoreSubscription = () => this.setState({ subscriptionChosen: false });

  ignore = () => this.setState({ ignore: true });

  onCompleteOutstandingEdits = (continueEditing) => {
    this.setState({ processingEdits: false, continueOnSave: false });
    // If we're just checking edits on log out we're in a subscription so we don't want to refresh
    if (continueEditing) return;
    this.onSelectSubscription(this.state.selectedSubscription, this.state.refresh);
  };

  rejectInvitation = (invitationIRI) => {
    const rejected = this.state.rejectedInvitations;
    this.setState({ rejectedInvitations: [...rejected, invitationIRI] });
  };

  cancelSubscription = (subscriptionIRI) => {
    const updated = [...this.state.cancelledSubscriptions, subscriptionIRI];
    this.setState({ cancelledSubscriptions: updated });
  };

  clearSubscription = (invitationIRI) => {
    GS.clearActiveSubscription();
    const rejected = this.state.rejectedInvitations;
    this.setState({
      rejectedInvitations: [...rejected, invitationIRI],
      subscriptionChosen: undefined,
      activeSubscription: undefined,
      activeSubscriptionName: "No subscription selected"
    });
  };

  onSelectSuccessfulSubscription = () => this.onSelectSubscription(this.state.newSubscription, true);

  onFailSubscription = () => this.setState({ success: false });

  onSelectSubscription = async (subscription, refresh = false) => {
    if (!subscription || subscription === undefined) {
      this.setState({
        isErrored: {
          message: "No subscription information available for selection."
        },
        fatal: true,
        actionable: true
      });
      return;
    }
    if (GS.outstandingEdits() > 0) {
      this.setState({ processingEdits: true, continueOnSave: false, selectedSubscription: subscription, refresh });
      return;
    }
    try {
      if (refresh) {
        const subscriptions = await GS.subscriptions();
        const invitations = await GS.invitations();
        await new Promise((resolve) => {
          this.setState({
            subscriptions,
            invitations,
            success: false
          }, () => resolve());
        });
      }
      let subItem = this.state.subscriptions.find(subscriptionItem => subscriptionItem.id === subscription);
      if (!subItem) subItem = this.state.invitations.find(invitation => invitation.subscription === subscription);
      if (!subItem) {
        this.setState({
          isErrored: {
            message: "Sorry, but we cannot find your subscription."
          },
          fatal: true,
          actionable: true
        });
        return;
      }
      const name = subItem.subscriptionName;
      const bucket = subItem.subscriptionBucket;
      const tripleCount = subItem.tripleCount;
      const subscriptionType = subItem.subscriptionType;
      const expirationDate = subItem.expirationDate;
      const modules = "module" in subItem ? subItem.module : 0;
      let maxTriplesAllowed = 0;
      let maxImportSize = 1000000;
      const subscriptionOptions = GS.subscriptionOptions();
      maxTriplesAllowed = subItem.maxTriples ?
        subItem.maxTriples
        :
        subscriptionOptions.find(option => option.subscriptionType === subscriptionType).maxTriples;
      maxImportSize = subItem.maxImportSize ?
        subItem.maxImportSize
        :
        subscriptionOptions.find(option => option.subscriptionType === subscriptionType).maxImportSize;
      const entitlements = subscription ?
        await GS.getEntitlements(this.state.user, subscription, bucket)
        :
        {};
      let ents = entitlements.hasEntitlement || [];
      let groups = entitlements.isMemberOfGroup || [];
      // We force the chosen sub to undefined so that nothing will render that might be clickable whilst setting the new sub
      this.setState({
        entitlements: ents,
        groups: groups,
        subscriptionChosen: undefined
      }, async () => {
        await GS.setActiveSubscription(subscription, name, bucket, subscriptionType, false, {
          maxImportSize
        });
        this.setState({
          tripleCount,
          firstVist: false,
          maxTriplesAllowed,
          modules,
          subscriptionChosen: true,
          activeSubscription: subscription,
          activeSubscriptionName: name,
          expirationDate
        });
      });
    }
    catch (e) {
      const text = e.message || "";
      console.log("Graphologi subscription selection error: " + text); // eslint-disable-line
      this.setState({
        isErrored: {
          message: e.message === "jwt expired" ?
            "Your session has expired. Please log in again to continue."
            :
            e.message.indexOf("Subscription is blocked.") === 0 ?
              e.message
              :
              e.message === "Subscription has failed." ?
                "Unfortunately there has been an issue with processing an operation on this subscription.\nThis subscription will be temporarily suspended whilst support investigates.\nThey will automatically be notified."
                :
                "Unfortunately we cannot retrieve the data necessary to use this subscription: " + text
        },
        sessionExpired: e.message === "jwt expired",
        fatal: true,
        firstVisit: false,
        isLoading: false,
        actionable: true
      });
    }
  };

  go = () => this.setState({ firstVisit: false });

  clearUp = () => this.setState({ success: false });

  render() {

    const { isErrored, isLoading, user, subscriptionChosen, maintenance, sessionExpired, expirationDate,
      newSubscription, tripleCount, maxTriplesAllowed, continueOnSave, lockedGraphs, loadKey,
      subscriptions, invitations, firstVisit, processingEdits, idle, success, loggingOut,
      importId, importComplete, rejectedInvitations, activeSubscription, modules } = this.state;
    const initialised = GS.isInitialised();
    const applicableInvitations = invitations.filter(invitation => invitation.status[0] !== STATUS_REJECTED
      && !rejectedInvitations.includes(invitation.id));

    return (
      <Router basename="gs">
        <MuiThemeProvider theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <UserProvider value={this.state}>
              {
                isErrored ?
                  <BasicLayout>
                    <Errored
                      dataTest="app-error"
                      hideFatal={sessionExpired}
                      data={this.state.isErrored}
                      actionable={this.state.actionable}
                      clearError={this.clearError}
                      logOut={this.logOut}
                      fatal={this.state.fatal} />
                  </BasicLayout>
                  :
                  loggingOut ?
                    <BasicBackground allowNavigation={false}>
                      <div style={{ marginTop: '200px', color: 'white', fontSize: '20px' }}>
                        <Typography align="center" data-test="goodbye">Goodbye for now.</Typography>
                      </div>
                    </BasicBackground>
                    :
                    idle ?
                      <Idle keep={this.keep} logOut={this.logOut} />
                      :
                      maintenance ?
                        <BasicBackground>
                          <Maintenance />
                        </BasicBackground>
                        :
                        processingEdits ?
                          <BasicLayout>
                            <Unsaved onDone={this.onCompleteOutstandingEdits} continue={continueOnSave} />
                          </BasicLayout>
                          :
                          importId ?
                            <BasicLayout>
                              <ImportWarningDialog complete={importComplete} onContinue={this.resetImport} />
                            </BasicLayout>
                            :
                            (
                              (isLoading || (user && !initialised)) ?
                                <BasicLayout height="calc(100% - 148px)">
                                  <Loading minHeight="100%" size="large" />
                                </BasicLayout>
                                :
                                (success ?
                                  <SubscriptionSuccess
                                    onGoHome={this.clearUp}
                                    resetIdle={this.resetIdle}
                                    subscription={newSubscription}
                                    onFail={this.onFailSubscription}
                                    onContinue={this.onSelectSuccessfulSubscription} />
                                  :
                                  (firstVisit && user ?
                                    <FirstVisit onNewSubscription={this.onNewSubscription} begin={this.go} onSelectSubscription={this.onSelectSubscription} />
                                    :
                                    (
                                      (subscriptionChosen === undefined &&
                                        ((subscriptions && subscriptions.length > 0) || (applicableInvitations && applicableInvitations.length > 0)) &&
                                        user ?
                                        <Switch>
                                          <SubscriptionSelectorDialog
                                            rejectedInvitations={rejectedInvitations}
                                            subscriptions={subscriptions.filter(subItem => subItem.approved)}
                                            onExitSelection={this.ignoreSubscription}
                                            onSelectSubscription={this.onSelectSubscription} />
                                        </Switch>
                                        :
                                        <Switch>
                                          <PrivateRoute exact path="/" component={ApplicationsManager}
                                            key={activeSubscription}
                                            activeSubscription={activeSubscription}
                                            logOut={this.logOut}
                                            modules={modules}
                                            expirationDate={expirationDate}
                                            tripleCount={tripleCount}
                                            maxTriplesAllowed={maxTriplesAllowed} />
                                          <Route exact path="/login" render={(props) => <Login {...props}
                                            ignoreSubscription={this.ignore}
                                            initialURL={this.state.initialURL}
                                            user={this.state.user}
                                            setUser={this.setUser} />} />
                                          {window.Graphologi.mode !== MANAGED_MODE &&
                                            <Route exact path="/signup" render={(props) => <Signup {...props} logOut={this.logOut} />} />
                                          }
                                          <Route exact path="/forgotten" render={(props) => <ForgottenPassword {...props} />} />
                                          <Route exact path="/forgottenusername" render={(props) => <ForgottenUsername {...props} />} />
                                          <Route exact path="/dpa" component={Dpa} />
                                          <Route path="/resetpassword" render={(props) => <ResetPassword {...props} />} />
                                          <Route exact path="/toolatetoreset" render={(props) => <TooLateToReset {...props} />} />
                                          <Route exact path="/getstarted" render={(props) => <GetStarted {...props} />} />
                                          {window.Graphologi.mode !== MANAGED_MODE &&
                                            <Route exact path="/toolate" render={(props) => <TooLate {...props} />} />
                                          }
                                          <Route exact path="/registrationerror" render={(props) => <RegistrationError {...props} />} />
                                          <PrivateRoute path="/administration" component={AdministrationApplication} />
                                          <PrivateRoute path="/account" component={AccountApplication}
                                            onSelectSubscription={this.onSelectSubscription}
                                            onNewSubscription={this.onNewSubscription}
                                          />
                                          <PrivateRoute path="/dashboard"
                                            entitlement={
                                              [
                                                APPENTITLEMENTS.MANAGE_SETTINGS,
                                                APPENTITLEMENTS.MANAGE_INTEGRATION,
                                                APPENTITLEMENTS.MANAGE_SNAPSHOTS,
                                                APPENTITLEMENTS.CAN_QUERY,
                                                APPENTITLEMENTS.EDIT_USER,
                                                APPENTITLEMENTS.EDIT_GROUP,
                                                APPENTITLEMENTS.CREATE_GROUP
                                              ]
                                            }
                                            component={DashboardApplication} />
                                          <PrivateRoute path="/vocabularymanager"
                                            entitlement={
                                              [
                                                ENTITLEMENTS.VIEW_TAXONOMY
                                              ]
                                            }
                                            lockedGraphs={lockedGraphs}
                                            loadKey={loadKey}
                                            component={VocabularyApplication} />
                                          <PrivateRoute path="/ontologymanager"
                                            entitlement={
                                              [
                                                ENTITLEMENTS.VIEW_ONTOLOGY
                                              ]
                                            }
                                            lockedGraphs={lockedGraphs}
                                            component={OntologyApplication} />
                                          <PrivateRoute path="/datamanager"
                                            entitlement={
                                              [
                                                ENTITLEMENTS.VIEW_DATASET
                                              ]
                                            }
                                            modules={modules}
                                            requiredModule={MODULES.DATA_MANAGER}
                                            lockedGraphs={lockedGraphs}
                                            loadKey={loadKey}
                                            component={DataApplication} />
                                          <Route render={() => <NotFound />} />
                                        </Switch>
                                      )
                                    )
                                  )
                                )
                            )
              }
            </UserProvider>
          </DndProvider>
        </MuiThemeProvider>
      </Router>
    );
  }
}

export default App;
